import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'analysis-walkthrough',
    category: 'analysis',
    title: 'Od nahrání po výsledek — průvodce na 15 minut',
    summary: 'Jeden kompletní průchod od začátku do konce: vytvoření společenství, nahrání souboru, získání prognózy a prvního výsledku optimalizace.',
    body: (
      <>
        <p>
          Tento článek vše propojuje. Projděte ho jednou a budete mít za sebou každý nástroj
          alespoň jednou. Předpokládá, že máte připravený soubor s hodinovou spotřebou jedné budovy.
        </p>

        <ol>
          <li>
            <strong>Vytvořte společenství.</strong> Přehled → <em>Společenství</em> →
            {' '}<em>Nové společenství</em>. Zadejte název, vyberte zemi a měnu. Uložte.
          </li>
          <li>
            <strong>Přidejte alespoň jednu budovu.</strong> Otevřete společenství → <em>Budovy</em> →
            {' '}<em>Přidat budovu</em>. Název budovy, typ a PSČ postačí pro začátek.
          </li>
          <li>
            <strong>Nahrajte soubor se zatížením.</strong> Společenství → <em>Správa dat</em> → zvolte
            {' '}<em>Building Load Profile</em>, vyberte budovu a přetáhněte CSV. Viz
            {' '}<ArticleLink to="data-upload-how">Nahrávání souboru krok za krokem</ArticleLink>, pokud
            je tato stránka pro vás nová.
          </li>
          <li>
            <strong>(Doporučeno) Nahrajte soubor s tarifem.</strong> Bez cen budou výsledky nákladů
            pouze orientační. Viz <ArticleLink to="data-tariff">Soubory s tarifem</ArticleLink>.
          </li>
          <li>
            <strong>Spusťte prognózu.</strong> Postranní panel společenství → <em>Prognóza</em>. Stránka má
            dvě záložky — <em>Krátkodobé (Day-Ahead)</em> a <em>Dlouhodobé (měsíční)</em>. Vyberte
            budovu a klikněte na <em>Spustit prognózu</em>. Viz
            {' '}<ArticleLink to="analysis-forecast">Spuštění prognózy</ArticleLink>.
          </li>
          <li>
            <strong>Spusťte optimalizaci.</strong> Postranní panel společenství → <em>Optimalizace</em> (samotná
            stránka je nadepsána „Optimalizace"). Zvolte cíl (např. <em>Minimalizovat náklady</em>)
            a klikněte na <em>Spustit optimalizaci Day-Ahead</em>. Viz
            {' '}<ArticleLink to="analysis-optimisation">Spuštění optimalizace</ArticleLink>.
          </li>
          <li>
            <strong>Přečtěte si výsledky.</strong> Čísla, grafy a úspory se zobrazí na stejné stránce.
            Význam jednotlivých KPI naleznete ve <ArticleLink to="results-glossary">slovníku pojmů</ArticleLink>;
            jak interpretovat každý graf je vysvětleno ve zbytku této sekce.
          </li>
        </ol>

        <Tip>
          Pokud je služba nedostupná nebo požadovaná data nebyla nahrána, platforma zobrazí
          jasně označené orientační výsledky. Pokud výsledek vypadá podezřele kulatě (např. přesně
          100 €/rok), zkontrolujte, zda stránka neobsahuje upozornění „příkladová data".
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-forecast',
    category: 'analysis',
    title: 'Spuštění prognózy',
    summary: 'Predikce spotřeby nebo výroby budovy či společenství na příští hodiny/dny.',
    body: (
      <>
        <p>
          <strong>Prognóza</strong> využívá vaše minulá data k předpovědi budoucího zatížení nebo výroby.
          Použijte ji k plánování provozu, k řízení optimalizace Day-Ahead nebo jednoduše k tomu,
          abyste viděli, jak bude vypadat zítřek — nebo příští čtvrtletí.
        </p>

        <h5 className="mt-3">Než spustíte prognózu</h5>
        <ul>
          <li>Společenství má alespoň jednu budovu s nahraným profilem <strong>Building Load Profile</strong>
            (více historie → lepší prognóza — pro spolehlivé prognózy se doporučují alespoň 3 měsíce
            dat, ideálně rok).</li>
          <li>Pro prognózy <em>solární</em> nebo <em>větrné</em> výroby potřebujete také soubor s výrobou
            (profil PV/větru společenství nebo střešní solár budovy). Soubor
            {' '}<ArticleLink to="data-weather">s počasím</ArticleLink> je volitelný, ale zlepšuje
            přesnost o 15–30 %.</li>
        </ul>

        <h5 className="mt-3">Krok za krokem</h5>
        <ol>
          <li>Otevřete společenství → v postranním panelu zvolte <strong>Prognózování</strong>.</li>
          <li>Vyberte <strong>režim</strong> pomocí dvou záložek v horní části stránky:
            <ul>
              <li><strong>Krátkodobé (Day-Ahead)</strong> — předpovídá příštích 24 hodin od zvoleného
                cílového data (až 7 dní dopředu).</li>
              <li><strong>Dlouhodobé (měsíční)</strong> — projektuje měsíční hodnoty na horizont, který
                zvolíte: <em>3 měsíce</em>, <em>6 měsíců</em> nebo <em>12 měsíců</em>.</li>
            </ul>
          </li>
          <li>Vyberte <strong>aktiva</strong>, pro která chcete prognózu: zatížení budovy, výrobu z FV
            společenství nebo výrobu z větru. Můžete zaškrtnout více než jedno.</li>
          <li>Klikněte na <strong>Spustit prognózu</strong> (popis tlačítka se přizpůsobí počtu
            vybraných aktiv). Během výpočtu se zobrazí indikátor průběhu <em>Probíhá prognóza…</em></li>
          <li>Výsledky se zobrazí v pravém panelu. Viz
            {' '}<ArticleLink to="results-forecast">Čtení grafu prognózy</ArticleLink>
            {' '}pro vysvětlení jednotlivých čar a pásem.</li>
        </ol>
        <ScreenshotPlaceholder
          img="forecast-page.png"
          description="The Forecasting page — the Short-Term / Long-Term tabs, the asset selector, and the Run Forecast button on the left; the result panel placeholder on the right."
          source="Platform — open any community → click 'Forecasting' in the sidebar. Capture the page before running anything, with both tabs visible."
          caption="Stránka Prognózování: vyberte záložku, vyberte aktiva, spusťte."
        />

        <Tip>
          Pokud stejná prognóza vypadá pokaždé velmi odlišně, pravděpodobně máte velmi málo
          vstupních dat. Přidejte více historie a spusťte znovu. Pokud výsledky stále vypadají
          špatně, viz
          {' '}<ArticleLink to="tb-forecast-flat">„Moje prognóza vypadá plochá nebo nesprávná"</ArticleLink>
          {' '}v části Řešení potíží.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-optimisation',
    category: 'analysis',
    title: 'Spuštění optimalizace (Modelování)',
    summary: 'Rozhodněte, kdy importovat, exportovat, nabíjet nebo vybíjet — s cílem minimalizovat náklady nebo maximalizovat soběstačnost.',
    body: (
      <>
        <p>
          Nástroj <strong>Optimalizace</strong> nalezne nejlepší plán pro flexibilní aktiva vašeho
          společenství — kdy nabíjet nebo vybíjet baterii, kdy nabíjet EV, kdy odebírat ze sítě
          a kdy do ní dodávat — podle cíle, který zvolíte.
        </p>

        <h5 className="mt-3">Než spustíte optimalizaci</h5>
        <ul>
          <li>Společenství s alespoň jedním nahraným souborem se <strong>zatížením</strong>.</li>
          <li>Soubor s <strong>tarifem</strong> (optimalizátor potřebuje ceny, aby věděl, co je levné
            a co drahé). Viz <ArticleLink to="data-tariff">Soubory s tarifem</ArticleLink>.</li>
          <li>Volitelné, ale doporučené: soubor s <strong>výrobou</strong> a jakákoli
            {' '}<strong>aktiva</strong> (PV, baterie, EV) zadaná pro společenství.</li>
        </ul>

        <h5 className="mt-3">Krok za krokem</h5>
        <ol>
          <li>Otevřete společenství → v postranním panelu zvolte <strong>Optimalizace</strong>. (Samotná
            stránka je nadepsána „Optimalizace" — je to stejné místo.)</li>
          <li>Zvolte <strong>cíl</strong>:
            <ul>
              <li><em>Minimalizovat náklady</em> — nakupujte při nízkých cenách, prodávejte při
                vysokých. Výchozí volba pro většinu společenství.</li>
              <li><em>Maximalizovat soběstačnost</em> — využívejte co nejvíce vlastní výroby,
                i kdyby byly náklady mírně vyšší.</li>
              <li><em>Maximalizovat vlastní spotřebu</em> — minimalizujte export vlastní výroby.</li>
            </ul>
          </li>
          <li>Optimalizace spouští jednodenní (24hodinový) plán pro příští den.</li>
          <li>Zkontrolujte seznam aktiv (PV, baterie, nabíječky EV). Nastavení baterie, jako je
            {' '}<em>minimální/maximální stav nabití</em> a <em>účinnost zpátečního chodu</em>, by měla
            odpovídat skutečnosti — pokud ne, změňte je.</li>
          <li>Klikněte na <strong>Spustit optimalizaci Day-Ahead</strong>. Zobrazí se indikátor průběhu
            <em>Probíhá optimalizace…</em>
            (může se zobrazit také zpráva „Řeší se MILP problém" — to je matematický řešič v akci).
            Spuštění pro několik budov obvykle trvá kratší dobu než minutu.</li>
          <li>Po dokončení se pod ním zobrazí panel s výsledky — karty KPI nahoře, hodinové grafy
            Energetické bilance a baterie níže. Viz
            {' '}<ArticleLink to="results-optimisation-kpis">Čtení KPI optimalizace</ArticleLink>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="modelling-page.png"
          description="The Modelling page (reached via the Optimization sidebar item) — objective selector (Minimise cost / Maximise self-sufficiency / Maximise self-consumption), the asset list, and the Run Optimization button."
          source="Platform — open any community → click 'Optimization' in the sidebar. (The page is titled 'Modelling'.) Capture before running, showing the objective radios and the asset list."
          caption="Stránka Optimalizace: vyberte cíl, zkontrolujte aktiva, spusťte."
        />

        <Warn>
          Pokud se spuštění ukončí s hlášením „infeasible", nelze splnit všechna omezení najednou
          (například baterie je příliš malá pro danou poptávku). Viz
          {' '}<ArticleLink to="tb-optimisation-infeasible">„Optimalizace hlásí infeasible"</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
